import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { useOffertePanelStore } from '../../stores/offerte-panel-store';
import { PackageChoice } from './PackageChoice';
import { AddressFieldsForm } from './AddressFields';
import { OfferteRouteList } from './OfferteRouteList';
import { OfferteCapacityBumpCard } from './OfferteCapacityBumpCard';

export function OffertePanel() {
  const isOpen = useOffertePanelStore((s) => s.isOpen);
  const offerte = useOffertePanelStore((s) => s.offerte);
  const selectedPackageSku = useOffertePanelStore((s) => s.selectedPackageSku);
  const selectPackage = useOffertePanelStore((s) => s.selectPackage);

  const phone = useOffertePanelStore((s) => s.phone);
  const setPhone = useOffertePanelStore((s) => s.setPhone);

  const pickupAddress = useOffertePanelStore((s) => s.pickupAddress);
  const updatePickup = useOffertePanelStore((s) => s.updatePickup);
  const useAlternateDelivery = useOffertePanelStore((s) => s.useAlternateDelivery);
  const toggleAlternateDelivery = useOffertePanelStore((s) => s.toggleAlternateDelivery);
  const deliveryAddress = useOffertePanelStore((s) => s.deliveryAddress);
  const updateDelivery = useOffertePanelStore((s) => s.updateDelivery);

  const petPackageReason = useOffertePanelStore((s) => s.petPackageReason);
  const setPetReason = useOffertePanelStore((s) => s.setPetReason);

  const routes = useOffertePanelStore((s) => s.routes);
  const selectedRouteId = useOffertePanelStore((s) => s.selectedRouteId);
  const selectRoute = useOffertePanelStore((s) => s.selectRoute);
  const visibleRoutesCount = useOffertePanelStore((s) => s.visibleRoutesCount);
  const showMoreRoutes = useOffertePanelStore((s) => s.showMoreRoutes);
  const isLoadingRoutes = useOffertePanelStore((s) => s.isLoadingRoutes);
  const routesError = useOffertePanelStore((s) => s.routesError);
  const openCapacityBump = useOffertePanelStore((s) => s.openCapacityBump);

  const notes = useOffertePanelStore((s) => s.notes);
  const setNotes = useOffertePanelStore((s) => s.setNotes);

  const bevestigingenSturen = useOffertePanelStore((s) => s.bevestigingenSturen);
  const setBevestigingenSturen = useOffertePanelStore((s) => s.setBevestigingenSturen);

  const isSubmitting = useOffertePanelStore((s) => s.isSubmitting);
  const submitResult = useOffertePanelStore((s) => s.submitResult);
  const submit = useOffertePanelStore((s) => s.submit);
  const close = useOffertePanelStore((s) => s.close);

  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, close]);

  if (!isOpen || !offerte) return null;

  const isPet = selectedPackageSku === 'WAS-PACK-VLOERKLEED-PET';
  const canSubmit =
    !isSubmitting &&
    !submitResult?.ok &&
    !!selectedPackageSku &&
    (!isPet || petPackageReason.trim().length >= 3);

  return (
    <>
      <div className="fixed inset-0 bg-black/30 z-40" onClick={close} />
      <div className="fixed top-0 right-0 h-full w-[520px] max-w-full bg-white shadow-2xl z-50 flex flex-col">
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold text-slate-900">Offerte accepteren</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              {offerte.leadnummer} — {offerte.customer.name}
            </p>
          </div>
          <button
            type="button"
            onClick={close}
            className="text-slate-400 hover:text-slate-600"
            aria-label="Sluiten"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {submitResult?.ok ? (
          <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
            <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center mb-3">
              <svg className="w-7 h-7 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-sm font-medium text-slate-900">
              {submitResult.message || 'Offerte geaccepteerd'}
            </p>
            {submitResult.workOrderId ? (
              <Link
                to={`/orders/${submitResult.workOrderId}`}
                onClick={close}
                className="mt-3 text-sm text-indigo-600 hover:underline"
              >
                Open order {submitResult.workOrderNumber || ''} →
              </Link>
            ) : (
              <p className="mt-2 text-xs text-slate-500">Werk Order wordt zo aangemaakt.</p>
            )}
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
              {submitResult && !submitResult.ok && (
                <div className="px-3 py-2.5 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
                  {submitResult.message}
                </div>
              )}

              <PackageChoice
                packages={offerte.packages}
                selectedSku={selectedPackageSku}
                onSelect={selectPackage}
              />

              {isPet && (
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-slate-600 block">
                    Reden voor huisdier-pakket <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={petPackageReason}
                    onChange={(e) => setPetReason(e.target.value)}
                    rows={2}
                    placeholder="Bijv. urinegeur, haren, kattenpis..."
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                  />
                </div>
              )}

              <div className="space-y-1.5">
                <label className="text-xs font-medium text-slate-600 block">Telefoonnummer</label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <AddressFieldsForm
                label="Ophaaladres"
                value={pickupAddress}
                onChange={updatePickup}
              />

              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={useAlternateDelivery}
                  onChange={toggleAlternateDelivery}
                  className="accent-indigo-600"
                />
                Ander afleveradres
              </label>

              {useAlternateDelivery && (
                <AddressFieldsForm
                  label="Afleveradres"
                  value={deliveryAddress}
                  onChange={updateDelivery}
                />
              )}

              <OfferteRouteList
                routes={routes}
                selectedId={selectedRouteId}
                onSelect={selectRoute}
                visibleCount={visibleRoutesCount}
                onShowMore={showMoreRoutes}
                isLoading={isLoadingRoutes}
                error={routesError}
                onBumpCapacity={openCapacityBump}
              />

              <OfferteCapacityBumpCard />

              <div className="space-y-1.5">
                <label className="text-xs font-medium text-slate-600 block">
                  Opmerkingen voor chauffeur
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Bijv. bel 10 min van tevoren..."
                  rows={2}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                />
              </div>

              {/* Bevestigingen */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 cursor-pointer text-sm">
                  <input
                    type="checkbox"
                    checked={bevestigingenSturen}
                    onChange={(e) => setBevestigingenSturen(e.target.checked)}
                    className="rounded border-slate-300"
                  />
                  <span className="font-medium">Bevestigingen direct sturen</span>
                </label>
                {!bevestigingenSturen && (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                    Geen S01/S03-mail bij creatie. Activeer later vanaf de order-pagina.
                  </p>
                )}
              </div>
            </div>

            <div className="px-5 py-4 border-t border-slate-200 bg-white flex gap-3">
              <button
                type="button"
                onClick={close}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
              >
                Annuleren
              </button>
              <button
                type="button"
                onClick={submit}
                disabled={!canSubmit}
                className={clsx(
                  'flex-1 px-4 py-2.5 text-sm font-medium rounded-lg transition-colors',
                  canSubmit
                    ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                    : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                )}
              >
                {isSubmitting ? 'Bezig...' : 'Accepteren & inplannen'}
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}
